import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { orderApi, menuApi } from '../services/api';
import type { Order as OrderType, OrderItem } from '../types';
import { roleTitles, roleSubtitles } from '../utils/dashboardMapping';

const ChefDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'preparing' | 'ready'>('pending');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      // Fetch all menu items first
      const menuItems = await menuApi.getMenuItems();
      const menuItemsMap = new Map(menuItems.map(item => [item.id, item]));

      const response = await orderApi.getOrders();
      const ordersWithItems = response.map(order => ({
        ...order,
        items: (order.items || []).map(item => ({
          ...item,
          menu_item_name: menuItemsMap.get(item.menu_item_id)?.name || 'Unknown Item'
        }))
      }));
      
      setOrders(ordersWithItems);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to fetch orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId: number, newStatus: 'preparing' | 'ready') => {
    try {
      await orderApi.updateOrderStatus(orderId, newStatus);
      await fetchOrders(); // Refresh orders after update
    } catch (error) {
      console.error('Error updating order status:', error);
      setError('Failed to update order status');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const filteredOrders = orders.filter(order => {
    if (activeTab === 'pending') return order.status === 'pending';
    if (activeTab === 'preparing') return order.status === 'preparing';
    if (activeTab === 'ready') return order.status === 'ready';
    return false;
  });

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-lg">
        <div className="p-6 border-b">
          <h1 className="text-2xl font-bold text-gray-800">{roleTitles.chef}</h1>
          <p className="text-sm text-gray-600 mt-1">{roleSubtitles.chef}</p>
        </div>
        <nav className="p-4">
          <button
            onClick={() => setActiveTab('pending')}
            className={`w-full text-left px-4 py-2 rounded-lg mb-2 ${
              activeTab === 'pending'
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Pending Orders
          </button>
          <button
            onClick={() => setActiveTab('preparing')}
            className={`w-full text-left px-4 py-2 rounded-lg mb-2 ${
              activeTab === 'preparing'
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            In Preparation
          </button>
          <button
            onClick={() => setActiveTab('ready')}
            className={`w-full text-left px-4 py-2 rounded-lg ${
              activeTab === 'ready'
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Ready for Pickup
          </button>
        </nav>
        <div className="absolute bottom-0 w-full p-4">
          <button
            onClick={handleLogout}
            className="w-full bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow">
            {isLoading ? (
              <div className="p-4 text-center">Loading orders...</div>
            ) : error ? (
              <div className="p-4 text-red-600">{error}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders.map((order) => (
                      <tr key={order.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#{order.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Table {order.table_number}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="mt-2 space-y-2">
                            {order.items?.map((item, index) => (
                              <div key={index} className="flex flex-col">
                                <div className="flex items-center">
                                  <span className="text-gray-900 font-medium">
                                    {item.menu_item_name} x{item.quantity}
                                  </span>
                                </div>
                                {item.notes && (
                                  <p className="text-gray-600 text-sm mt-1 pl-2 border-l-2 border-gray-200">
                                    {item.notes}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'preparing' ? 'bg-blue-100 text-blue-800' :
                            order.status === 'ready' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(order.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.status === 'pending' && (
                            <button
                              onClick={() => handleUpdateStatus(order.id, 'preparing')}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              Start Preparing
                            </button>
                          )}
                          {order.status === 'preparing' && (
                            <button
                              onClick={() => handleUpdateStatus(order.id, 'ready')}
                              className="text-green-600 hover:text-green-800"
                            >
                              Mark Ready
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChefDashboard; 